import { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Checkbox } from '@/components/ui/checkbox'
import { 
  Search, 
  FileText, 
  CheckCircle2, 
  ChevronRight,
  Sparkles,
  Target,
  AlertCircle,
  Loader2,
  ArrowLeft
} from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import templateService, { ObjectiveTemplate, ObjectiveTemplateObjective } from '@/services/api/templateService'
import toast from 'react-hot-toast'

interface TemplateObjectiveSelectionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onObjectivesSelected: (objectives: ObjectiveTemplateObjective[], templateInfo: { id: number; name: string }) => void
  onBack?: () => void
}

export default function TemplateObjectiveSelectionDialog({
  open,
  onOpenChange,
  onObjectivesSelected,
  onBack
}: TemplateObjectiveSelectionDialogProps) {
  const [loading, setLoading] = useState(false)
  const [templates, setTemplates] = useState<ObjectiveTemplate[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedTemplate, setSelectedTemplate] = useState<ObjectiveTemplate | null>(null)
  const [selectedObjectives, setSelectedObjectives] = useState<Set<string>>(new Set())

  useEffect(() => {
    if (open) {
      loadTemplates()
    }
  }, [open])

  const loadTemplates = async () => {
    try {
      setLoading(true)
      const response = await templateService.getPublishedTemplates({ limit: 100 })
      setTemplates(response.items)
      
      if (response.items.length === 0) {
        toast('No published templates available yet', { icon: 'ℹ️' })
      }
    } catch (error) {
      console.error('Error loading templates:', error)
      toast.error('Failed to load templates')
    } finally {
      setLoading(false)
    }
  }

  const filteredTemplates = templates.filter(template => {
    const searchLower = searchQuery.toLowerCase()
    return (
      template.name.toLowerCase().includes(searchLower) ||
      template.description?.toLowerCase().includes(searchLower) ||
      template.role_type?.toLowerCase().includes(searchLower)
    )
  })

  const handleTemplateSelect = (template: ObjectiveTemplate) => {
    setSelectedTemplate(template)
    setSelectedObjectives(new Set())
  }

  const handleObjectiveToggle = (objectiveId: string) => {
    const newSelected = new Set(selectedObjectives)
    if (newSelected.has(objectiveId)) {
      newSelected.delete(objectiveId)
    } else {
      newSelected.add(objectiveId)
    }
    setSelectedObjectives(newSelected)
  }

  const handleSelectAll = () => {
    if (!selectedTemplate?.objectives_data?.objectives) return
    
    const allIds = selectedTemplate.objectives_data.objectives
      .map((obj, idx) => `${selectedTemplate.id}-${idx}`)
    
    if (selectedObjectives.size === allIds.length) {
      setSelectedObjectives(new Set())
    } else {
      setSelectedObjectives(new Set(allIds))
    }
  }

  const handleConfirm = async () => {
    if (!selectedTemplate || selectedObjectives.size === 0) {
      toast.error('Please select at least one objective')
      return
    }

    const objectives = selectedTemplate.objectives_data?.objectives || []
    const selectedObjs = objectives.filter((_, idx) => 
      selectedObjectives.has(`${selectedTemplate.id}-${idx}`)
    )

    // Increment template usage
    try {
      await templateService.incrementUsage(selectedTemplate.id)
    } catch (error) {
      console.warn('Failed to increment template usage:', error)
    }

    onObjectivesSelected(selectedObjs, {
      id: selectedTemplate.id,
      name: selectedTemplate.name
    })
    
    // Reset state
    setSelectedTemplate(null)
    setSelectedObjectives(new Set())
    setSearchQuery('')
  }

  const handleBackToTemplates = () => {
    setSelectedTemplate(null)
    setSelectedObjectives(new Set())
  }

  const handleClose = () => {
    setSelectedTemplate(null)
    setSelectedObjectives(new Set())
    setSearchQuery('')
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] p-0">
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <FileText className="h-6 w-6 text-purple-600" />
            {selectedTemplate ? 'Select Objectives' : 'Choose a Template'}
          </DialogTitle>
          <DialogDescription>
            {selectedTemplate 
              ? `Select the objectives you want to add from "${selectedTemplate.name}"`
              : 'Browse published templates and select objectives for your impact & growth plan'
            }
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 pb-6">
          <AnimatePresence mode="wait">
            {!selectedTemplate ? (
              // Template List View
              <motion.div
                key="template-list"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="space-y-4"
              >
                {/* Search Bar */}
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search templates by name, role, or description..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>

                {/* Templates List */}
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
                  </div>
                ) : filteredTemplates.length === 0 ? (
                  <div className="text-center py-12">
                    <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">
                      {searchQuery ? 'No templates match your search' : 'No published templates available'}
                    </p>
                  </div>
                ) : (
                  <ScrollArea className="h-[500px] pr-4">
                    <div className="space-y-3">
                      {filteredTemplates.map((template) => {
                        const objectiveCount = template.objectives_data?.objectives?.length || 0
                        
                        return (
                          <motion.div
                            key={template.id}
                            whileHover={{ scale: 1.01 }}
                            whileTap={{ scale: 0.99 }}
                          >
                            <Card
                              className="cursor-pointer hover:border-purple-300 hover:shadow-md transition-all"
                              onClick={() => handleTemplateSelect(template)}
                            >
                              <CardHeader className="pb-3">
                                <CardTitle className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <span className="text-lg">{template.name}</span>
                                    {template.usage_count > 0 && (
                                      <Badge variant="secondary" className="text-xs">
                                        <Sparkles className="h-3 w-3 mr-1" />
                                        Used {template.usage_count}x
                                      </Badge>
                                    )}
                                  </div>
                                  <ChevronRight className="h-5 w-5 text-gray-400" />
                                </CardTitle>
                              </CardHeader>
                              <CardContent className="space-y-3">
                                <p className="text-sm text-gray-600">{template.description}</p>
                                
                                <div className="flex items-center gap-4 text-sm">
                                  {template.role_type && (
                                    <Badge variant="outline">{template.role_type}</Badge>
                                  )}
                                  {template.functional_area && (
                                    <Badge variant="outline">{template.functional_area}</Badge>
                                  )}
                                  <div className="flex items-center gap-1 text-purple-600">
                                    <Target className="h-4 w-4" />
                                    <span className="font-medium">{objectiveCount} objectives</span>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          </motion.div>
                        )
                      })}
                    </div>
                  </ScrollArea>
                )}

                {/* Footer */}
                <div className="flex justify-between pt-4 border-t">
                  {onBack ? (
                    <Button variant="outline" onClick={onBack}>
                      <ArrowLeft className="h-4 w-4 mr-2" />
                      Back to Objective Sources
                    </Button>
                  ) : (
                    <div />
                  )}
                  <Button variant="outline" onClick={handleClose}>
                    Cancel
                  </Button>
                </div>
              </motion.div>
            ) : (
              // Objective Selection View
              <motion.div
                key="objective-selection"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                {/* Selected Template Info */}
                <Card className="bg-purple-50 border-purple-200">
                  <CardContent className="pt-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-purple-900">{selectedTemplate.name}</h3>
                        <p className="text-sm text-purple-700 mt-1">{selectedTemplate.description}</p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleBackToTemplates}
                      >
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Change Template
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Select All Checkbox */}
                <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="select-all"
                      checked={
                        selectedObjectives.size === 
                        (selectedTemplate.objectives_data?.objectives?.length || 0)
                      }
                      onCheckedChange={handleSelectAll}
                    />
                    <label
                      htmlFor="select-all"
                      className="text-sm font-medium cursor-pointer"
                    >
                      Select All ({selectedTemplate.objectives_data?.objectives?.length || 0} objectives)
                    </label>
                  </div>
                  <Badge variant="secondary">
                    {selectedObjectives.size} selected
                  </Badge>
                </div>

                {/* Objectives List */}
                <ScrollArea className="h-[400px]">
                  <div className="space-y-2 pr-4">
                    {selectedTemplate.objectives_data?.objectives?.map((objective, index) => {
                      const objectiveId = `${selectedTemplate.id}-${index}`
                      const isSelected = selectedObjectives.has(objectiveId)

                      return (
                        <motion.div
                          key={objectiveId}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05 }}
                        >
                          <Card
                            className={`cursor-pointer transition-all ${
                              isSelected
                                ? 'border-purple-500 bg-purple-50'
                                : 'hover:border-purple-200'
                            }`}
                            onClick={() => handleObjectiveToggle(objectiveId)}
                          >
                            <CardContent className="pt-4">
                              <div className="flex items-start gap-3">
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={() => handleObjectiveToggle(objectiveId)}
                                  className="mt-1"
                                />
                                <div className="flex-1 space-y-2">
                                  <div className="flex items-start justify-between gap-2">
                                    <h4 className="font-medium text-gray-900">{objective.title}</h4>
                                    {isSelected && (
                                      <CheckCircle2 className="h-5 w-5 text-purple-600 flex-shrink-0" />
                                    )}
                                  </div>
                                  <p className="text-sm text-gray-600">{objective.description}</p>
                                  <div className="flex flex-wrap gap-2">
                                    {objective.kpaName && (
                                      <Badge variant="outline" className="text-xs">
                                        {objective.kpaName}
                                      </Badge>
                                    )}
                                    {objective.pillar && (
                                      <Badge variant="secondary" className="text-xs">
                                        {typeof objective.pillar === 'string' ? objective.pillar : objective.pillar.name}
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        </motion.div>
                      )
                    })}
                  </div>
                </ScrollArea>

                {/* Footer Actions */}
                <div className="flex justify-between pt-4 border-t">
                  <Button variant="outline" onClick={handleBackToTemplates}>
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back
                  </Button>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={handleClose}>
                      Cancel
                    </Button>
                    <Button
                      onClick={handleConfirm}
                      disabled={selectedObjectives.size === 0}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Add {selectedObjectives.size} Objective{selectedObjectives.size !== 1 ? 's' : ''}
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </DialogContent>
    </Dialog>
  )
}
